/* eslint-disable no-param-reassign */
import { ValuationApproachGPC } from 'api/models/ValuationApproachGPC';
import { FlattenedData } from 'pages/Valuations/approaches/guidelinePublicCompanies/utils/types';
import { GPCExtended, ValuationApproachGPCExtended } from '../../GuidelinePublicCompanies.props';

/**
 * Set default metrics enablement for a flattened GPC comparison.
 * Mutates the input object to enable all metric flags.
 *
 * @param flatComp - The flattened comparison object to modify
 */
export const resetDefaultMetricsEnablement = (flatComp: FlattenedData): void => {
  flatComp.first_metric_enabled = true;
  flatComp.second_metric_enabled = true;
  flatComp.first_forward_metric_enabled = true;
  flatComp.second_forward_metric_enabled = true;
};

/**
 * Filter out deleted comparisons and re-index remaining ones.
 * Excludes both newly deleted comparisons and those previously removed in this session.
 *
 * @param allComparisons - All current comparisons in the approach
 * @param deletedCapIqIds - Set of Capital IQ IDs being deleted in this operation
 * @param alreadyRemovedCapIqIds - Set of Capital IQ IDs already removed in this session
 * @returns Filtered and re-indexed comparisons array
 */
export const filterAndReindexComparisons = (
  allComparisons: GPCExtended[],
  deletedCapIqIds: Set<string | null | undefined>,
  alreadyRemovedCapIqIds: Set<string>
): GPCExtended[] =>
  allComparisons
    .filter(
      (comparison: GPCExtended) =>
        !deletedCapIqIds.has(comparison.cap_iq_id) && !alreadyRemovedCapIqIds.has(comparison.cap_iq_id ?? '')
    )
    .map((comp, index) => ({
      ...comp,
      order: index,
    })) as GPCExtended[];

/**
 * Remove comp group associations from surviving companies that shared a comp group with deleted companies.
 * Mutates the input array to remove gpc_approach_comp_group and comp_group_id fields.
 *
 * @param comparisons - Array of surviving comparisons to clean
 * @param deletedCompGroupIds - Set of comp group IDs that were deleted
 */
export const cleanupCompGroupAssociations = (comparisons: GPCExtended[], deletedCompGroupIds: Set<number>): void => {
  comparisons.forEach(company => {
    if (deletedCompGroupIds.has(company.gpc_approach_comp_group as number)) {
      delete company.gpc_approach_comp_group;
    }
    if (company.comp_group_id && deletedCompGroupIds.has(company.comp_group_id)) {
      delete company.comp_group_id;
    }
  });
};

/**
 * Track deleted comparisons in the ref for backend persistence on save.
 * Appends deletion metadata to the ref for each deleted comparison.
 *
 * @param removedGPCsRef - Ref storing all removed comparisons in this session
 * @param deletedComparisons - Array of comparisons being deleted
 */
export const trackDeletedComparisons = (
  removedGPCsRef: React.RefObject<{ capitalIQId: string; gpcComparisonsId?: number }[]>,
  deletedComparisons: GPCExtended[]
): void => {
  deletedComparisons.forEach(deletedComparison => {
    removedGPCsRef.current.push({
      capitalIQId: deletedComparison.cap_iq_id!,
      gpcComparisonsId: deletedComparison.id,
    });
  });
};

/**
 * Build the final approach update payload with deleted comparison tracking and comp group cleanup.
 * Constructs the complete valuations_approach_gpc object ready for updateApproach.
 *
 * @param gpc - Current GPC approach data
 * @param filteredComparisons - Comparisons remaining after deletion
 * @param valuationKeysToSave - Comp group associations to keep
 * @param currentDeletedCompGroups - Previously deleted comp groups
 * @param newDeletedCompGroups - Newly deleted comp group IDs
 * @param removedGPCsRef - Ref containing all removed comparisons
 * @param approachId - Approach ID (undefined for new approaches)
 * @returns Complete approach update payload
 */
export const buildApproachUpdatePayload = (
  gpc: ValuationApproachGPCExtended | undefined,
  filteredComparisons: GPCExtended[],
  valuationKeysToSave: any[],
  currentDeletedCompGroups: number[],
  newDeletedCompGroups: number[],
  removedGPCsRef: React.RefObject<{ capitalIQId: string; gpcComparisonsId?: number }[]>,
  approachId?: number
): any => ({
  ...(gpc as ValuationApproachGPC),
  valuationapproachgpccompgroup_set: valuationKeysToSave,
  gpc_comparison: filteredComparisons,
  // Add deleted_gpc_comparisons only in existing approaches
  // since they do not make sense in brand new ones
  deleted_gpc_comparisons: approachId
    ? (removedGPCsRef.current.map(item => item.gpcComparisonsId).filter(Boolean) as number[])
    : [],
  deleted_comp_groups: newDeletedCompGroups.length
    ? ([...currentDeletedCompGroups, ...newDeletedCompGroups] as number[])
    : currentDeletedCompGroups,
});
